<template>
  <div>
    <p>
      Fyll inn nødvendig informasjon relatert til utleier
      <b>{{ landlord.meta.needsAcknowledgement.landlordName }}</b
      >.
    </p>

    <ValidationObserver
      v-if="
        landlord.meta.needsAcknowledgement.nationalIdentityNumber ||
        landlord.meta.needsAcknowledgement.organizationNumber
      "
      v-slot="{ invalid }"
    >
      <div v-if="landlord.meta.needsAcknowledgement.nationalIdentityNumber" class="my-4">
        <ValidationProvider
          slim
          ref="nationalIdentityNumberRef"
          name="Fødselsnummer"
          rules="required|no-valid-social-security-number"
          v-slot="{ errors }"
        >
          <hl-Input
            v-model="nationalIdentityNumber"
            :error="errors[0]"
            errorVariant="warning"
            label="Fødselsnummer"
            :placeholder="landlord.meta.needsAcknowledgement.nationalIdentityNumber.previousValue"
          />
        </ValidationProvider>
      </div>

      <div v-if="landlord.meta.needsAcknowledgement.organizationNumber" class="my-4">
        <ValidationProvider
          slim
          ref="organizationNumberRef"
          name="Organisasjonsnummer"
          rules="required|digits:9|no-valid-organization-number"
          v-slot="{ errors }"
        >
          <hl-Input
            v-model="organizationNumber"
            :error="errors[0]"
            @input="fetchCompanyName($event)"
            errorVariant="warning"
            label="Organisasjonsnummer"
            :placeholder="landlord.meta.needsAcknowledgement.organizationNumber.previousValue"
          />
        </ValidationProvider>

        <hl-Alert class="my-4" variant="info" v-if="companyName && isCompanyNameValid">
          Vær oppmerksom på at ved å oppdatere organisasjonsnummeret vil det også endre firmanavnet til:
          <b>{{ companyName }}</b
          >.
        </hl-Alert>
      </div>

      <hl-Button
        variant="primary"
        :disabled="invalid || loading || (companyName && !isCompanyNameValid)"
        :loading="loading"
        @click="submitLandlordInfo"
      >
        Bekreft
      </hl-Button>
    </ValidationObserver>
  </div>
</template>

<script>
import { mapActions } from 'vuex';
import Input from '@modules/husleie-framework/src/components/forms/inputs/input/Input.vue';
import Button from '@modules/husleie-framework/src/components/buttons/Button.vue';
import Alert from '@modules/husleie-framework/src/components/text/Alert.vue';

export default {
  components: {
    'hl-Input': Input,
    'hl-Button': Button,
    'hl-Alert': Alert,
  },

  data() {
    return {
      nationalIdentityNumber: null,
      organizationNumber: null,
      companyName: null,
      loading: false,
    };
  },

  props: {
    landlord: {
      type: Object,
      required: true,
    },
  },

  computed: {
    isCompanyNameValid() {
      return this.companyName != null && this.companyName !== 'Ugyldig organisasjonsnummer';
    },
  },

  methods: {
    ...mapActions(['patchLandlordProfileById']),

    async submitLandlordInfo() {
      this.loading = true;
      try {
        const data = {
          acknowledgementToken: this.landlord.meta.needsAcknowledgement.token,
        };
        if (this.organizationNumber) {
          data.organizationNumber = this.organizationNumber;
        }
        if (this.nationalIdentityNumber) {
          data.nationalIdentityNumber = this.nationalIdentityNumber;
        }
        if (this.companyName) {
          data.name = this.companyName;
        }
        await this.patchLandlordProfileById({
          landlordId: this.landlord.id,
          data,
        });
        await this.$emit('updated');
      } catch (error) {
        this.$toast('Kunne ikke lagre', 'error');
      }
      this.loading = false;
    },

    fetchCompanyName(e) {
      const axios = require('axios');
      // Make a request for a user with a given ID
      if (e.match(/^\d\d\d\d\d\d\d\d\d$/)) {
        axios
          .get('/api/enhetsregisteret/' + e)
          .then(response => {
            this.companyName = response.data.navn;
          })
          .catch(function (error) {
            console.error(error);
          });
      }
    },
  },

  mounted() {
    if (this.landlord.meta.needsAcknowledgement.organizationNumber) {
      this.organizationNumber = this.landlord.meta.needsAcknowledgement.organizationNumber.previousValue;
      this.$nextTick(() => {
        this.$refs.organizationNumberRef.applyResult({
          errors: [this.landlord.meta.needsAcknowledgement.organizationNumber.error],
          valid: false,
          failedRules: {},
        });
      });
    }

    if (this.landlord.meta.needsAcknowledgement.nationalIdentityNumber) {
      this.nationalIdentityNumber = this.landlord.meta.needsAcknowledgement.nationalIdentityNumber.previousValue;
      this.$nextTick(() => {
        this.$refs.nationalIdentityNumberRef.applyResult({
          errors: [this.landlord.meta.needsAcknowledgement.nationalIdentityNumber.error],
          valid: false,
          failedRules: {},
        });
      });
    }

    this.$nextTick(() => {
      if (
        this.landlord.meta.needsAcknowledgement.organizationNumber &&
        this.landlord.meta.needsAcknowledgement.organizationNumber.previousValue
      ) {
        this.fetchCompanyName(this.landlord.meta.needsAcknowledgement.organizationNumber.previousValue);
      }
    });
  },
};
</script>

<style scoped lang="scss"></style>
